import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useQuery, UseQueryResult } from "react-query";

const PAGESIZE = 20;

export function useEntityTimeMachine({
  orgId,
  type,
  entityUuid,
}: Omit<
  EntityTimeMachineContext,
  "query" | "setPagination" | "dataRef"
>): EntityTimeMachineContext {
  const [{ page, pageSize }, setPagination] = useState({
    page: 1,
    pageSize: PAGESIZE,
  });
  useEffect(() => setPagination({ page, pageSize }), [page, pageSize]);
  const query = useQuery(
    ["entityTimeMachine", { orgId, type, entityUuid, page, pageSize }],
    async () =>
      fetch(`/api/${orgId}/entity-time-machine`, {
        method: "POST",
        body: JSON.stringify({
          orgId,
          entityUuid,
          type,
          pagination: { page, pageSize },
        }),
      }).then((res) => res.json()),
    {
      keepPreviousData: true,
      staleTime: 60_000,
    }
  );
  const dataRef = useRef(query.data);
  dataRef.current = query.data || dataRef.current;
  return { orgId, entityUuid, type, query, dataRef, setPagination };
}

export type EntityTimeMachineContext = {
  orgId: string;
  entityUuid: string;
  type: string;
  query: UseQueryResult<any>;
  dataRef: React.MutableRefObject<any>;
  setPagination: React.Dispatch<
    React.SetStateAction<{
      page: number;
      pageSize: number;
    }>
  >;
};

export const entityTimeMachineContext = createContext<EntityTimeMachineContext>(
  undefined as unknown as EntityTimeMachineContext
);

export function useEntityTimeMachineContext() {
  return useContext(entityTimeMachineContext);
}
